import { apis } from '@doltech/core/lib/api/api.config';
import { getUserAnonymousId } from '@doltech/core/lib/auth/constants';
import { sum } from 'lodash';
import { flow, types } from 'mobx-state-tree';
import { useStore } from 'mobx-store-provider';
import { apiPublicUrls } from '@doltech/core/lib/api/api.public-urls';

const UserAnswer = types
  .model('UserAnswer', {
    questionId: types.identifier,
    answerId: types.string,
    submitted: types.optional(types.boolean, false),
  })
  .actions((self) => ({
    setAnswer(answer: string) {
      self.answerId = answer;
    },
  }));

const QuizStatisticAnswer = types.model('QuizStatisticAnswer', {
  answerId: types.identifier,
  noOfSelections: types.number,
});

const QuizStatistic = types
  .model('QuizStatistic', {
    questionId: types.identifier,
    answers: types.map(QuizStatisticAnswer),
  })
  .views((self) => ({
    get countTotalAnswers() {
      if (self.answers) {
        return sum(
          [...self.answers.values()].map((answer) => {
            return answer.noOfSelections;
          })
        );
      }
      return 0;
    },
  }));

export const ExerciseQuizDoTestStore = types
  .model('ExerciseQuizStoreDoTest')
  .props({
    id: types.identifier,
    userAnswers: types.optional(types.map(UserAnswer), {}),
    retryCount: 0,
    quizCount: 0,
    quizIds: types.optional(types.array(types.string), []),
    currentQuizIndex: 0,
    isLoginRequired: false,
    statistics: types.map(QuizStatistic),

    submitting: false,
  })
  .actions((self) => ({
    setQuizIds(quizIds: string[]) {
      if (quizIds) {
        self.quizIds.replace(quizIds);
      }
    },
    setQuizCount(totalQuestions: number) {
      if (totalQuestions) {
        self.quizCount = totalQuestions;
      }
    },
    setStatistics(value) {
      self.statistics.replace(value);
    },
    nextQuizIndex() {
      self.currentQuizIndex++;
    },
    prevQuizIndex() {
      self.currentQuizIndex--;
    },
    setQuizIndex(value) {
      self.currentQuizIndex = value;
    },
    resetQuizIndex() {
      self.currentQuizIndex = 0;
    },
    setUserAnswers(answers) {
      self.userAnswers.replace(answers);
    },
    answer(questionId: string, optionId: string) {
      self.userAnswers.set(questionId, { questionId, answerId: optionId });
    },
  }))
  .views((self) => ({
    preparePayload() {
      return {
        exerciseId: self.id,
        type: 'QUIZ',
        userAnswers: self.userAnswers,
      };
    },
    canSubmit({ questionId }) {
      return !self.submitting && Boolean(self.userAnswers.get(questionId));
    },
  }))
  .actions((self) => ({
    check: flow(function* checkQuizAnswer({ pageId, questionId }) {
      // Ref: https://dolenglish.atlassian.net/wiki/spaces/DOLTECH/pages/882475009/BE+Design+-+DLE
      // Note from BE: Submit only one questionId per request (array with 1 element).

      self.submitting = true;
      const payload = self.preparePayload();
      const currentUserAnswer = payload.userAnswers.get(questionId);
      const submitPayload = {
        ...payload,
        pageId,
        isCompleted: false,
        userAnswers: [
          {
            questionId: currentUserAnswer.questionId,
            answerId: currentUserAnswer.answerId,
          },
        ],
      };
      let result = null;
      const isPreview = !pageId;
      if (isPreview) {
        const allAnswers = [...payload.userAnswers.values()];
        // for preview version on admin side => we mock the data of statistics so we can show which is correct or wrong;
        result = {
          ...submitPayload,
          userAnswers: allAnswers,
          statistics: allAnswers.map((ua) => {
            return {
              questionId: ua.questionId,
              answers: [{ answerId: ua.answerId, noOfSelections: 1 }],
            };
          }),
        };
      } else {
        try {
          const res = yield apis.publicOnlineTestV2.post(
            apiPublicUrls.onlineTestV2.EXERCISES_PROGRESS,
            {
              ...submitPayload,
              sessionId: getUserAnonymousId(),
            }
          );
          result = res.data;
          // eslint-disable-next-line no-empty
        } catch (error) {}
      }

      self.submitting = false;
      return result;
    }),
  }));

export const useExerciseQuizDoTestStore = () => useStore(ExerciseQuizDoTestStore);
